/**
 * Tooltip — 의존성 없는 경량 호버 툴팁 (Notion 스타일 다크 라벨).
 *
 * 아이콘 단독 버튼처럼 역할이 시각적으로 드러나지 않는 컨트롤에 사용.
 * 네이티브 title 속성은 노출 지연이 길고 스타일 제어가 불가해 별도 구현
 * (프로젝트에 radix tooltip 미설치 — 의존성 최소화 원칙).
 *
 * 사용:
 *   <Tooltip label="완료 처리"><Button ... /></Tooltip>            // 단문(하위호환, nowrap)
 *   <Tooltip content={<>...여러 줄 설명...</>}><Info /></Tooltip>   // [P-2] ReactNode·자동 줄바꿈·max-width
 *
 * 하위호환: label 만 넘기면 기존 동작(단일 라인 nowrap) 그대로 유지된다.
 * content 를 넘기면 자동 줄바꿈 + max-width(기본 320) 다중 라인 모드로 렌더한다.
 * 키보드 접근: 트리거(children)가 포커스 가능하면 focus 시 표시된다(InfoTooltip 참고).
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Info } from 'lucide-react';
import { colors, spacing, borderRadius, shadows, typography, breakpoints } from '../../styles/design-tokens';

// 툴팁 본문 최대 폭 기본값 — 모바일 세로 브레이크포인트(320px) 토큰에서 파생
const DEFAULT_CONTENT_MAX_WIDTH = parseInt(breakpoints.xs, 10) || 320;

export interface TooltipProps {
  /** 단문 문구 (하위호환 — 단일 라인 nowrap) */
  label?: string;
  /** [P-2] ReactNode 본문 — 지정 시 label 대신 렌더(자동 줄바꿈·max-width) */
  content?: React.ReactNode;
  /** content 모드 최대 폭(px) — 기본 320(토큰 파생) */
  maxWidth?: number;
  /** 표시 지연 (ms) — 기본 250 */
  delayMs?: number;
  /** 위치 — 기본 top */
  side?: 'top' | 'bottom';
  children: React.ReactNode;
}

export const Tooltip: React.FC<TooltipProps> = ({
  label,
  content,
  maxWidth,
  delayMs = 250,
  side = 'top',
  children,
}) => {
  const [visible, setVisible] = useState(false);
  const timerRef = useRef<number | null>(null);

  const clear = useCallback(() => {
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const show = useCallback(() => {
    clear();
    timerRef.current = window.setTimeout(() => setVisible(true), delayMs);
  }, [clear, delayMs]);

  const hide = useCallback(() => {
    clear();
    setVisible(false);
  }, [clear]);

  useEffect(() => clear, [clear]);

  // content(ReactNode) 모드: 자동 줄바꿈 + max-width + 좌측 정렬. label 모드: 기존 nowrap 유지.
  const isRich = content != null;
  const body = isRich ? content : label;
  const boxMaxWidth = isRich ? (maxWidth ?? DEFAULT_CONTENT_MAX_WIDTH) : 320;

  /**
   * ⚠️ 툴팁은 반드시 document.body 로 포털한다.
   * 이전 구현은 트리거 옆에 position:absolute 로 붙였는데, 트리거가
   * overflow:auto/hidden 컨테이너(가로 스크롤 표 등) 안에 있으면 툴팁이 잘려
   * 내용이 보이지 않았다. 스크롤 컨테이너는 클리핑 컨텍스트를 만들기 때문이다.
   */
  const [coords, setCoords] = useState<{ left: number; top: number; placement: 'top' | 'bottom' } | null>(null);
  const anchorRef = useRef<HTMLSpanElement | null>(null);

  const measure = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const GAP = 6;
    // 위쪽 공간이 부족하면 아래로 뒤집는다 (대략적인 높이 추정치 사용)
    const estimatedHeight = isRich ? 140 : 28;
    const placement: 'top' | 'bottom' =
      side === 'top' && r.top < estimatedHeight + GAP ? 'bottom' : side;

    const centerX = r.left + r.width / 2;
    // 좌우 화면 밖으로 나가지 않도록 클램프
    const half = boxMaxWidth / 2;
    const left = Math.min(Math.max(centerX, half + 8), window.innerWidth - half - 8);

    setCoords({
      left,
      top: placement === 'top' ? r.top - GAP : r.bottom + GAP,
      placement,
    });
  }, [side, isRich, boxMaxWidth]);

  // 표시 중에는 스크롤·리사이즈에 따라 위치를 따라간다
  useEffect(() => {
    if (!visible) return;
    measure();
    const onMove = () => measure();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [visible, measure]);

  const tooltipNode = visible && body != null && coords ? (
    <span
      role="tooltip"
      style={{
        position: 'fixed',
        left: coords.left,
        top: coords.top,
        transform: coords.placement === 'top' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
        background: colors.secondary,
        color: colors.text.onPrimary,
        fontSize: isRich ? typography.body.small.fontSize : 11.5,
        fontWeight: 500,
        lineHeight: 1.5,
        padding: isRich ? `${spacing.sm} ${spacing.md}` : '4px 8px',
        borderRadius: borderRadius.md,
        whiteSpace: isRich ? 'normal' : 'nowrap',
        textAlign: isRich ? 'left' : 'center',
        maxWidth: boxMaxWidth,
        width: isRich ? 'max-content' : undefined,
        wordBreak: 'keep-all',
        zIndex: 10000,
        pointerEvents: 'none',
        boxShadow: shadows.base,
      }}
    >
      {body}
    </span>
  ) : null;

  return (
    <span
      ref={anchorRef}
      style={{ position: 'relative', display: 'inline-flex' }}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {tooltipNode && typeof document !== 'undefined'
        ? createPortal(tooltipNode, document.body)
        : null}
    </span>
  );
};

/**
 * InfoTooltip — 차트·표 제목 옆 ⓘ(Info) 트리거. [F2]
 * 포커스 가능(role=button·tabIndex 0)해 키보드로도 툴팁을 열 수 있다.
 * content 는 ReactNode(다중 라인 집계 기준 설명).
 */
export interface InfoTooltipProps {
  content: React.ReactNode;
  side?: 'top' | 'bottom';
  maxWidth?: number;
  /** 접근성 라벨 — 기본 "집계 기준 설명" */
  ariaLabel?: string;
  /** E2E/식별용 */
  testId?: string;
  /** 아이콘 크기(px) — 기본 14 */
  size?: number;
}

export const InfoTooltip: React.FC<InfoTooltipProps> = ({
  content,
  side = 'top',
  maxWidth,
  ariaLabel = '집계 기준 설명',
  testId,
  size = 14,
}) => (
  <Tooltip content={content} side={side} maxWidth={maxWidth}>
    <span
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      data-testid={testId}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: colors.text.muted,
        cursor: 'help',
        borderRadius: borderRadius.full,
        outlineOffset: 2,
        flex: '0 0 auto',
      }}
    >
      <Info size={size} aria-hidden="true" />
    </span>
  </Tooltip>
);

export default Tooltip;
